import { Divider, Tabs, Typography } from 'antd';
import { sanitizeHTML } from '../../utils';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Logo from '../../Logo';
import DraftEmail from './draftEmail';
import './styles.css';

interface MeetingRecording {
  agent_id: string;
  lead_id?: string;
  twilio_call_sid: string;
  transcription?: string;
  transcription_segments?: Array<{ text: string; start: number; end: number }>;
  appointment_id?: string;
  agent: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  lead?: {
    source_id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
  };
  prompt_result?: {
    summary: string;
    note_heading: string;
    bullet_summary: string[];
    detailed_notes: string;
    agent_action_items: Array<{
      quoted_source_text: string[];
      title: string;
      text: string;
      description: string;
      due_datetime: string;
    }>;
    appointment_outcome: string;
    email_draft: EmailDraft;
  };
  note_id?: string;
  created_at: string;
  duration: number;
}

export interface EmailDraft {
  subject: string;
  body: string;
}

// Format time in MM:SS format
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export default function HWRecordingPage(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _props: {
    apiUrl: string;
  },
): JSX.Element {
  const { id: rawId } = useParams<{ id: string }>();
  // Clean the ID by removing any trailing punctuation and ensuring it's a valid UUID
  const id = rawId ? rawId.replace(/[.,;:!?]+$/, '') : '';
  const [recording, setRecording] = useState<MeetingRecording | null>(null);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [loading, setLoading] = useState<boolean>(true);

  // Validate if the string is a valid UUID
  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Fetch meeting recording data from API
  useEffect(() => {
    const fetchMeetingRecording = async () => {
      if (!id || !isValidUUID(id)) {
        console.error('Invalid or missing UUID:', id);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `${_props.apiUrl}/api/meeting-recording/${id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch meeting recording: ${response.statusText}`,
          );
        }

        const data: MeetingRecording = await response.json();
        setRecording(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching meeting recording:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchMeetingRecording();
  }, [id, _props.apiUrl]);

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (!recording) {
    return <div className="error-container">Recording not found</div>;
  }

  return (
    <div className="recording-container" style={{ textAlign: 'left' }}>
      <Logo size={0.75} />
      <Divider style={{ margin: '12px 0' }} />
      <Typography.Title
        level={2}
        style={{ fontWeight: '300', fontSize: '28px', lineHeight: '36px' }}
      >
        {recording.lead
          ? `${recording.prompt_result?.note_heading} - ${recording.lead.first_name} ${recording.lead.last_name}`
          : recording.prompt_result?.note_heading}
      </Typography.Title>
      <div style={{ textAlign: 'left' }}>
        <Typography.Text
          style={{ fontSize: '14px', lineHeight: '150%', fontWeight: 400 }}
        >
          {recording.created_at
            ? new Date(recording.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : ''}
          {recording.duration
            ? ` - ${Math.floor(recording.duration / 60)}:${(recording.duration % 60).toString().padStart(2, '0')}`
            : ''}
        </Typography.Text>
      </div>
      {/* <div className="recording-meta" style={{ textAlign: 'left' }}>
        {recording.prompt_result?.summary}
      </div> */}
      <div className="recording-content">
        {/* Mobile view */}
        {window.innerWidth < 768 ? (
          <div className="mobile-view">
            <Tabs
              activeKey={activeTab}
              className="recording-tabs"
              items={[
                {
                  key: 'summary',
                  label: 'Summary',
                  children: (
                    <div
                      className="tab-content"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(
                          recording.prompt_result?.detailed_notes || '',
                        ),
                      }}
                    />
                  ),
                },
                {
                  key: 'transcript',
                  label: 'Transcript',
                  children: (
                    <div className="tab-content">
                      {recording.transcription_segments
                        ? recording.transcription_segments.map(
                            (segment, index) => (
                              <div className="transcript-message" key={index}>
                                <Typography.Text
                                  style={{ fontWeight: 'normal' }}
                                >
                                  <span className="transcript-time">
                                    {formatTime(segment.start)}
                                  </span>
                                  {segment.text}
                                </Typography.Text>
                              </div>
                            ),
                          )
                        : recording.transcription
                            ?.split('\n')
                            .map((message, index) => (
                              <div className="transcript-message" key={index}>
                                <Typography.Text
                                  style={{ fontWeight: 'normal' }}
                                >
                                  {message}
                                </Typography.Text>
                              </div>
                            ))}
                    </div>
                  ),
                },
                {
                  key: 'email',
                  label: 'Email draft',
                  disabled: !recording?.prompt_result?.email_draft,
                  children: recording?.prompt_result?.email_draft ? (
                    <DraftEmail
                      emailDraft={recording.prompt_result.email_draft}
                      leadEmail={recording.lead?.email || ''}
                    />
                  ) : null,
                },
              ]}
              onChange={setActiveTab}
            />
          </div>
        ) : (
          <div
            className="desktop-only"
            style={{ display: 'flex', gap: '24px' }}
          >
            <div style={{ flex: 1 }}>
              <h3>Summary</h3>
              <div
                className="tab-content"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(
                    recording?.prompt_result?.detailed_notes || '',
                  ),
                }}
              />
              {recording?.prompt_result?.email_draft && (
                <DraftEmail
                  emailDraft={recording?.prompt_result?.email_draft}
                  leadEmail={recording.lead?.email || ''}
                />
              )}
            </div>
            <div>
              <Divider style={{ height: '100%' }} type="vertical" />
            </div>
            <div style={{ flex: 1 }}>
              <h3>Transcript</h3>
              <div className="tab-content">
                {recording?.transcription_segments
                  ? recording?.transcription_segments?.map((segment, index) => (
                      <div className="transcript-message" key={index}>
                        <Typography.Text style={{ fontWeight: 'normal' }}>
                          <span className="transcript-time">
                            {formatTime(segment.start)}
                          </span>
                          {segment.text}
                        </Typography.Text>
                      </div>
                    ))
                  : recording?.transcription
                      ?.split('\n')
                      .map((message, index) => (
                        <div className="transcript-message" key={index}>
                          <Typography.Text style={{ fontWeight: 'normal' }}>
                            {message}
                          </Typography.Text>
                        </div>
                      ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
